import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, LogOut, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/blog/admin/all")
      .then((r) => setPosts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
    await api.delete(`/blog/${id}`);
    load();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">/admin — content management</div>
          <h1 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Dashboard
          </h1>
          <p className="text-[#94A3B8] mt-2 font-mono text-sm">Signed in as {user?.email}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="px-4 py-2.5 border border-[#272E3A] hover:border-[#FF5500] text-slate-300 hover:text-white font-mono text-xs uppercase tracking-widest flex items-center gap-2 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
          <Link
            to="/admin/new"
            data-testid="admin-new-post-btn"
            className="px-5 py-2.5 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> New Post
          </Link>
        </div>
      </div>

      <div className="bg-[#13161C] border border-[#272E3A]">
        <div className="grid grid-cols-[1fr_120px_120px_140px] gap-4 px-6 py-3 border-b border-[#272E3A] font-mono text-xs uppercase tracking-widest text-[#94A3B8]">
          <div>Title</div>
          <div>Status</div>
          <div>Updated</div>
          <div className="text-right">Actions</div>
        </div>

        {loading && (
          <div className="p-10 text-center font-mono text-xs uppercase tracking-widest text-[#94A3B8]">Loading...</div>
        )}
        {!loading && posts.length === 0 && (
          <div className="p-10 text-center text-[#94A3B8]" data-testid="admin-empty">
            No posts yet. Create your first one.
          </div>
        )}

        <div className="divide-y divide-[#272E3A]">
          {posts.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-row-${p.slug}`}
              className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_140px] gap-4 px-6 py-4 items-center hover:bg-[#1C2028] transition-colors"
            >
              <div>
                <Link to={`/blog/${p.slug}`} className="text-white font-semibold hover:text-[#FF5500] transition-colors">
                  {p.title}
                </Link>
                <div className="text-xs text-[#94A3B8] font-mono mt-1 truncate">/{p.slug}</div>
              </div>
              <div className="font-mono text-xs flex items-center gap-1.5">
                {p.published ? (
                  <span className="text-[#10B981] flex items-center gap-1.5"><Eye className="w-3 h-3" /> PUBLIC</span>
                ) : (
                  <span className="text-[#94A3B8] flex items-center gap-1.5"><EyeOff className="w-3 h-3" /> DRAFT</span>
                )}
              </div>
              <div className="font-mono text-xs text-[#94A3B8]">
                {new Date(p.updated_at).toLocaleDateString()}
              </div>
              <div className="flex gap-2 justify-start md:justify-end">
                <Link
                  to={`/admin/edit/${p.id}`}
                  data-testid={`edit-post-btn-${p.slug}`}
                  aria-label={`Edit post ${p.title}`}
                  className="p-2 border border-[#272E3A] hover:border-[#FF5500] text-slate-300 hover:text-[#FF5500] transition-colors"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleDelete(p.id, p.title)}
                  data-testid={`delete-post-btn-${p.slug}`}
                  aria-label={`Delete post ${p.title}`}
                  className="p-2 border border-[#272E3A] hover:border-[#EF4444] text-slate-300 hover:text-[#EF4444] transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
